import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ArrowRight, Database } from "lucide-react";
import ImportAssessmentModal from "@/components/ImportAssessmentModal";
import { MOCK_ASSESSMENT_ID } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtD } from "@/lib/platform";

const AssessmentCard = ({ a, onOpen }) => (
  <Card className="grc-card border-slate-200 shadow-sm cursor-pointer hover:border-[#008080]/40 transition-colors"
        onClick={onOpen} data-testid={`open-assessment-${a.assessment_id}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-xs font-mono text-slate-500">{a.industry || "—"} · imported {fmtD(a.imported_at)}</div>
          <div className="text-lg font-semibold text-[#003366] mt-1 truncate">{a.org_name || "Untitled organization"}</div>
          <div className="text-xs text-slate-500 font-mono mt-1 truncate">{a.assessment_id}</div>
        </div>
        <ArrowRight className="w-5 h-5 text-slate-400 shrink-0" />
      </div>
      <div className="mt-4 flex items-center gap-3 text-xs text-slate-600 font-mono">
        <span>Score {a.overall_score ?? "—"}</span>·<span>L{a.maturity_level ?? "—"}</span>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [open, setOpen] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const nav = useNavigate();
  const auth = useAuth();
  const canImport = auth?.user?.role === "admin";

  useEffect(() => {
    api.get("/platform/assessments").then(({ data }) => setAssessments(data.assessments)).catch(() => {});
  }, [auth?.user?.tenant_id]);

  return (
    <div className="App min-h-screen">
      <Header />
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8 space-y-8" data-testid="dashboard-page">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono text-slate-500 uppercase tracking-wide" data-testid="dashboard-tenant-name">
              {auth?.user?.tenant_name || "Assessments"}
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-[#003366] mt-1">Your compliance workspace</h1>
            <p className="text-sm text-slate-600 mt-1">Select an assessment or import one from App A.</p>
          </div>
          {canImport && (
            <Button className="grc-btn bg-[#008080] hover:bg-[#008080]/90" onClick={() => setOpen(true)}
                    data-testid="open-import-modal-button">
              <Download className="w-4 h-4 mr-2" /> Import from App A
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tenant-assessment-grid">
          {assessments.map((a) => (
            <AssessmentCard key={a.assessment_id} a={a} onOpen={() => nav(`/assessment/${a.assessment_id}/dashboard`)} />
          ))}

          <Card className="grc-card border-slate-200 shadow-sm cursor-pointer hover:border-[#008080]/40 transition-colors"
                onClick={() => nav(`/assessment/${MOCK_ASSESSMENT_ID}/dashboard`)}
                data-testid={`open-assessment-${MOCK_ASSESSMENT_ID}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-mono text-slate-500">MOCK · DEMO</div>
                  <div className="text-lg font-semibold text-[#003366] mt-1">Acme Fintech Pvt Ltd</div>
                  <div className="text-xs text-slate-500 font-mono mt-1">{MOCK_ASSESSMENT_ID}</div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
              <div className="mt-4 flex items-center gap-3 text-xs text-slate-600 font-mono">
                <span>Score 62</span>·<span>Developing</span>·<span>SDF: Provisional</span>
              </div>
            </CardContent>
          </Card>

          {canImport && (
            <Card className="grc-card border-dashed border-slate-300 bg-slate-50/60">
              <CardContent className="p-5 flex flex-col items-start gap-3">
                <div className="w-9 h-9 rounded-[8px] bg-white border border-slate-200 flex items-center justify-center">
                  <Database className="w-4 h-4 text-slate-500" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#003366]">
                    {assessments.length ? "Import another assessment" : "Import your first assessment"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Pull a V2.2 payload from dpdp.codexero.ai</div>
                </div>
                <Button variant="secondary" size="sm" className="grc-btn" onClick={() => setOpen(true)} data-testid="cta-import-empty">
                  Import
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ImportAssessmentModal
        open={open}
        onOpenChange={setOpen}
        onImported={(res) => nav(`/assessment/${res.assessment_id}/dashboard`)}
      />
    </div>
  );
}
